import React, { useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Card,
  CardMedia,
  CardContent,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
} from "@mui/material";
import { Share, ArrowBack } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import * as Constant from "../features/constant";
import {
  useGetBlogDetailQuery,
  useGetRecommendedBlogsQuery,
} from "../features/api/blogApiSlice";

const categoryColors = {
  ESPORTS: "#3b82f6",
  GUIDELINE: "#22c55e",
  CARD: "#f97316",
  SLOT_GAMES: "#a855f7",
  CRASH_GAMES: "#ef4444",
  VIP: "#eab308",
};

const BlogDetail = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { slug } = useParams();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { data: detailData, isLoading } = useGetBlogDetailQuery(slug);
  const { data: recommendedData } = useGetRecommendedBlogsQuery();

  const post = detailData?.result;
  const recommendedBlogs = (recommendedData?.result || []).filter(
    (b) => b.id !== post?.id
  ).slice(0, 3);

  useEffect(() => {
    if (post?.metaTitle || post?.title) {
      document.title = post.metaTitle || post.title;
    }
    return () => {
      document.title = "";
    };
  }, [post]);

  useEffect(() => {
    if (post?.seoScript) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.textContent = post.seoScript;
      document.head.appendChild(script);
      return () => {
        document.head.removeChild(script);
      };
    }
  }, [post?.seoScript]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post?.title,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!post) {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.background.secondaryPaper,
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="h6"
          sx={{ color: process.env.REACT_APP_COLOR_TEXT || "white" }}
        >
          {t("blog.notfound", "Blog post not found.")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.secondaryPaper,
        minHeight: "80vh",
        pt: isMobile ? 8 : 3,
        pb: 4,
      }}
    >
      {/* Banner */}
      {post.bannerImageUrl && (
        <Box
          sx={{
            width: "100%",
            maxHeight: 400,
            overflow: "hidden",
            mb: 3,
          }}
        >
          <Box
            component="img"
            src={post.bannerImageUrl}
            alt={post.title}
            sx={{
              width: "100%",
              height: "100%",
              maxHeight: 400,
              objectFit: "cover",
            }}
          />
        </Box>
      )}

      <Container maxWidth="lg">
        {/* Back button */}
        <Box sx={{ mb: 2 }}>
          <IconButton
            onClick={() => navigate(Constant.getPath("/blog"))}
            sx={{ color: process.env.REACT_APP_COLOR_TEXT || "white" }}
          >
            <ArrowBack />
          </IconButton>
        </Box>

        <Grid container spacing={4}>
          {/* Main content */}
          <Grid item xs={12} md={8}>
            {/* Category + Meta */}
            <Box sx={{ mb: 2 }}>
              <Chip
                label={post.category?.replace(/_/g, " ")}
                size="small"
                sx={{
                  backgroundColor: categoryColors[post.category] || "#666",
                  color: "white",
                  fontWeight: 600,
                  fontSize: "11px",
                  mb: 1,
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  color: process.env.REACT_APP_COLOR_TEXT || "white",
                  fontWeight: 700,
                  lineHeight: 1.3,
                  mb: 1,
                  fontSize: isMobile ? "1.5rem" : "2rem",
                }}
              >
                {post.title}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: process.env.REACT_APP_COLOR_TEXT || "white",
                    opacity: 0.6,
                  }}
                >
                  {post.author && `${post.author} · `}
                  {formatDate(post.publishedAt)}
                </Typography>
                <IconButton size="small" onClick={handleShare} sx={{ color: "grey.500" }}>
                  <Share fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 3 }} />

            {/* Blog content */}
            <Box
              sx={{
                color: process.env.REACT_APP_COLOR_TEXT || "white",
                fontSize: "15px",
                lineHeight: 1.8,
                "& a": {
                  color: theme.palette.primary.main,
                  textDecoration: "underline",
                },
                "& img": {
                  maxWidth: "100%",
                  height: "auto",
                  borderRadius: "8px",
                  my: 2,
                },
                "& p": { mb: 2 },
                "& h2, & h3": {
                  color: process.env.REACT_APP_COLOR_TEXT || "white",
                  mt: 3,
                  mb: 1,
                },
              }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(post.content || ""),
              }}
            />
          </Grid>

          {/* Sidebar — Popular Blog */}
          <Grid item xs={12} md={4}>
            {post.popularBlogs && post.popularBlogs.length > 0 && (
              <Box
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: 2,
                  p: 2,
                  mb: 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: process.env.REACT_APP_COLOR_TEXT || "white",
                    fontWeight: 600,
                    fontSize: "16px",
                    mb: 1,
                  }}
                >
                  {t("blog.popular", "Popular Blog")}
                </Typography>
                <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 1 }} />
                <List disablePadding>
                  {post.popularBlogs.map((pb) => (
                    <ListItemButton
                      key={pb.id}
                      onClick={() => navigate(Constant.getPath(`/blog/${pb.slug}`))}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        px: 1,
                      }}
                    >
                      {pb.thumbnailImageUrl && (
                        <Box
                          component="img"
                          src={pb.thumbnailImageUrl}
                          alt={pb.title}
                          sx={{
                            width: 50,
                            height: 35,
                            objectFit: "cover",
                            borderRadius: 1,
                            mr: 1.5,
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <ListItemText
                        primary={pb.title}
                        primaryTypographyProps={{
                          fontSize: "13px",
                          color: process.env.REACT_APP_COLOR_TEXT || "white",
                          lineHeight: 1.3,
                          sx: {
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          },
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            )}
          </Grid>
        </Grid>

        {/* You May Also Like */}
        {recommendedBlogs.length > 0 && (
          <Box sx={{ mt: 5 }}>
            <Typography
              variant="h6"
              sx={{
                color: process.env.REACT_APP_COLOR_TEXT || "white",
                fontWeight: 600,
                mb: 3,
              }}
            >
              {t("blog.recommended", "You May Also Like")}
            </Typography>
            <Grid container spacing={3}>
              {recommendedBlogs.map((blog) => (
                <Grid item xs={12} sm={6} md={4} key={blog.id}>
                  <Card
                    onClick={() => navigate(Constant.getPath(`/blog/${blog.slug}`))}
                    sx={{
                      cursor: "pointer",
                      backgroundColor: theme.palette.background.paper,
                      borderRadius: 2,
                      overflow: "hidden",
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
                      },
                    }}
                  >
                    <CardMedia
                      component="img"
                      height={140}
                      image={blog.thumbnailImageUrl || blog.bannerImageUrl || ""}
                      alt={blog.title}
                      sx={{ objectFit: "cover" }}
                    />
                    <CardContent>
                      <Chip
                        label={blog.category?.replace(/_/g, " ")}
                        size="small"
                        sx={{
                          backgroundColor: categoryColors[blog.category] || "#666",
                          color: "white",
                          fontWeight: 600,
                          fontSize: "10px",
                          mb: 1,
                        }}
                      />
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: process.env.REACT_APP_COLOR_TEXT || "white",
                          fontWeight: 600,
                          lineHeight: 1.3,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {blog.title}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default BlogDetail;
